import { Controller } from "@hotwired/stimulus";
import { renderStreamMessage } from "@hotwired/turbo";

const TURBO_STREAM = "text/vnd.turbo-stream.html";

/**
 * Posts the element's fields to its URL once the page that carries it is Shown (docs/adr/0027-a-visit-is-recorded-once-its-page-is-shown.md).
 * Each connect is a show, Back and Forward included; Turbo's momentary cached preview is not.
 * A morph that keeps the element posts again only when its URL or fields changed.
 */
export default class extends Controller<HTMLElement> {
   static values = {
      url: String,
      fields: Array,
   };

   declare readonly urlValue: string;
   declare readonly fieldsValue: [string, string][];

   // The send this connection posted, or null until it posts. Disconnect clears it, so the next connect posts again.
   private lastSent: string | null = null;
   private readonly sendAfterPreview = () => this.send();

   connect(): void {
      this.send();
   }

   disconnect(): void {
      this.lastSent = null;
      document.removeEventListener("turbo:load", this.sendAfterPreview);
   }

   urlValueChanged(): void {
      this.sendWhenChanged();
   }

   fieldsValueChanged(): void {
      this.sendWhenChanged();
   }

   // Value callbacks also run once before connect, while lastSent is still null; only a change after this
   // connection posted (a morph) sends.
   private sendWhenChanged(): void {
      if (this.lastSent !== null)
         this.send();
   }

   private send(): void {
      // A cached preview is on screen only until the fresh page replaces it. When the fresh page morphs this element
      // in place instead of connecting a new one, turbo:load sends for it.
      if (document.documentElement.hasAttribute("data-turbo-preview")) {
         document.addEventListener("turbo:load", this.sendAfterPreview, { once: true });
         return;
      }

      const key = JSON.stringify([this.urlValue, this.fieldsValue]);
      if (key === this.lastSent)
         return;

      this.lastSent = key;
      void this.post();
   }

   private async post(): Promise<void> {
      const body = new URLSearchParams();
      for (const [name, value] of this.fieldsValue)
         body.append(name, value);

      try {
         const response = await fetch(this.urlValue, {
            method: "POST",
            credentials: "same-origin",
            headers: { Accept: `${TURBO_STREAM}, text/html` },
            body,
         });

         if (!response.ok)
            return;

         if (response.headers.get("Content-Type")?.startsWith(TURBO_STREAM))
            renderStreamMessage(await response.text());
      } catch {
         // A failed send is not retried; the next show sends again.
      }
   }
}
